import {
    DownOutlined,
    FullscreenOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import { Card, DatePicker, Modal, Select, Spin } from "antd";
import React, { useEffect, useState } from "react";
import {
    dateRangePresets,
    disableFutureDates,
    formatDate,
} from "../../services/util";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { SupportTicketTable } from "./SupportTicketTable";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
dayjs.extend(customParseFormat);

const PIE_COLORS = [
    "#3B82F6",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#06B6D4",
];

/**
 * rawData from API: [ [header_label, header_count], [name, count], ... ]
 * Transform to recharts format: [ { name, value }, ... ]
 */
function transformPieData(rawData) {
    if (!rawData || rawData.length < 2) return [];
    return rawData.slice(1).map(([name, value]) => ({
        name: String(name),
        value: Number(value),
    }));
}

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div
                style={{
                    background: "#1e2433",
                    border: "1px solid #2e3650",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#e0e6f0",
                }}
            >
                <p style={{ margin: 0, fontWeight: 600 }}>{payload[0].name}</p>
                <p style={{ margin: 0, color: payload[0].payload.fill }}>
                    Count: {payload[0].value}
                </p>
                <p style={{ margin: 0, color: "#8a94b0" }}>
                    {(payload[0].percent * 100).toFixed(1)}%
                </p>
            </div>
        );
    }
    return null;
};

const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
}) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x}
            y={y}
            fill="white"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={12}
            fontWeight={600}
        >
            {`${(percent * 100).toFixed(1)}%`}
        </text>
    );
};

function IssueComponentPieChart({ data, height = 300, onSliceClick }) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <PieChart>
                <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    outerRadius={height * 0.35}
                    dataKey="value"
                    labelLine={false}
                    label={renderCustomLabel}
                    onClick={(entry) => onSliceClick && onSliceClick(entry)}
                    style={{ cursor: onSliceClick ? "pointer" : "default" }}
                >
                    {data.map((_, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{ color: "#8a94b0", fontSize: 12 }}
                    formatter={(value) => (
                        <span style={{ color: "#c0c8e0" }}>{value}</span>
                    )}
                />
            </PieChart>
        </ResponsiveContainer>
    );
}

function IssueComponentChart() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [chartData, setChartData] = useState([]);
    const [enableZoom, setEnableZoom] = useState(false);
    const [hotelListOptions, setHotelListOptions] = useState([]);
    const [selectedHotels, setSelectedHotels] = useState([]);
    const [selectedComponentForTableView, setSelectedComponentForTableView] =
        useState("");
    const [formData, setFormData] = useState({
        start_date: formatDate(dayjs().subtract(7, "day")),
        end_date: formatDate(dayjs()),
        staff_id,
        hotel_ids: selectedHotels,
    });

    const {
        status,
        data,
        refetch: getHotels,
    } = useQuery(
        "hotels",
        async () => await HTTP("/dashboard/getHotels", formData),
        { enabled: false },
    );

    const { data: ComponentWiseTopIssueRate, refetch } = useQuery(
        "TopIssueComponentChart",
        async () =>
            await HTTP(`/dashboard/getTopIssuesComponentsHotelWise`, {
                ...formData,
            }),
        { enabled: false },
    );

    useEffect(() => {
        getHotels();
        refetch();
    }, []);

    useEffect(() => {
        refetch();
    }, [formData]);

    useEffect(() => {
        if (status === "success") setHotelListOptions(data.data);
    }, [status, data]);

    useEffect(() => {
        setChartData(ComponentWiseTopIssueRate?.data || []);
    }, [ComponentWiseTopIssueRate]);

    useEffect(() => {
        setFormData((prev) => ({ ...prev, hotel_ids: selectedHotels }));
    }, [selectedHotels]);

    const pieData = transformPieData(chartData);
    const isLoading = pieData.length === 0;

    return (
        <Card className="box-shadow" style={{ width: "45%" }}>
            <div className="flex space-between">
                <strong>Top 5 Issue Components</strong>
                <div className="flex flex-gap-10 align-center">
                    <DatePicker.RangePicker
                        defaultValue={[
                            dayjs(formData.start_date),
                            dayjs(formData.end_date),
                        ]}
                        value={[
                            dayjs(formData.start_date),
                            dayjs(formData.end_date),
                        ]}
                        allowClear={false}
                        disabledDate={disableFutureDates}
                        format="MM/DD/YYYY"
                        presets={dateRangePresets.filter(
                            (preset) =>
                                preset.label !== "Today" &&
                                preset.label !== "Yesterday",
                        )}
                        onChange={(e) => {
                            setFormData({
                                ...formData,
                                start_date: formatDate(e[0]),
                                end_date: formatDate(e[1]),
                            });
                        }}
                    />
                    <Select
                        defaultValue={selectedHotels}
                        value={selectedHotels}
                        suffixIcon={<DownOutlined />}
                        allowClear
                        mode="multiple"
                        className="select-container"
                        placeholder="Hotel"
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onChange={(e) => setSelectedHotels(e)}
                        loading={hotelListOptions.length === 0}
                        style={{ width: "250px" }}
                    >
                        {hotelListOptions.map((option) => (
                            <Select.Option
                                key={option.hotel_id}
                                value={option.hotel_id}
                            >
                                {option.hotel_name}
                            </Select.Option>
                        ))}
                    </Select>
                    <FullscreenOutlined
                        style={{ fontSize: "20px" }}
                        onClick={() => setEnableZoom(true)}
                    />
                </div>
            </div>

            {isLoading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "60px 0",
                    }}
                >
                    <Spin
                        indicator={
                            <LoadingOutlined style={{ fontSize: 48 }} spin />
                        }
                    />
                </div>
            ) : (
                <IssueComponentPieChart
                    data={pieData}
                    height={300}
                    onSliceClick={(entry) =>
                        setSelectedComponentForTableView(entry.name)
                    }
                />
            )}

            {/* Fullscreen Modal */}
            <Modal
                open={enableZoom}
                destroyOnHidden
                onCancel={() => setEnableZoom(false)}
                width="70vw"
                footer={null}
            >
                <div style={{ padding: "16px 0" }}>
                    <strong style={{ fontSize: 16 }}>
                        Top 5 Issue Components
                    </strong>
                    <IssueComponentPieChart data={pieData} height={500} />
                </div>
            </Modal>

            {/* Drill-down Table Modal */}
            <Modal
                open={selectedComponentForTableView !== ""}
                destroyOnHidden
                onCancel={() => setSelectedComponentForTableView("")}
                width="fit-content"
                footer={null}
            >
                <SupportTicketTable
                    component_name={selectedComponentForTableView}
                    start_date={formData.start_date}
                    end_date={formData.end_date}
                />
            </Modal>
        </Card>
    );
}

export default IssueComponentChart;
